import React, { useCallback, useMemo, useRef } from 'react'
import { Field, Formik } from 'formik'
import { makeStyles } from '@material-ui/core'
import Checkbox from '@material-ui/core/Checkbox'
import CheckBoxIcon from '@material-ui/icons/CheckBox'
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank'
import DeleteIcon from '@material-ui/icons/Delete'
import DragIndicatorIcon from '@material-ui/icons/DragIndicator'
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd'
import { filter, uniqueId } from 'lodash'
import yup from 'civic-champs-shared/utils/yup'
import { FormModal, FormModalScreen } from 'components/FormModal'
import RadioGroupFormField from 'components/RadioGroupFormField'
import { CkEditorStyledField } from 'civic-champs-shared/formik/components/CkEditorStyledField'
import AdvancedAutocomplete from 'civic-champs-shared/core/AdvancedAutocomplete'
import { InlineAddButton } from 'civic-champs-shared/core/InlineAddButton'
import { useErrorNotification } from 'civic-champs-shared/api/hooks'
import { useFeatureEnabled } from 'core/feature/hooks'
import useOrganizationCredentials from 'credential/hooks/useOrganizationCredentials'
import useQuestionnairesCollection from 'question-sets/hooks/useQuestionnairesCollection'
import { Questionnaire, QuestionnaireStatus } from 'question-sets/types'
import { useSetGroupCredentials } from 'group/hooks/useSetGroupCredentials'
import { useSetGroupQuestionSets } from 'group/hooks/useSetGroupQuestionSets'
import {
  areGroupApplicationCredentialsDirty,
  areGroupApplicationFormsDirty,
  areGroupApplicationInstructionsDirty,
  areGroupApplicationQuestionSetsDirty,
  areGroupApplicationValuesDirty,
  buildGroupApplicationInitialValues,
  buildGroupApplicationInstructionsUpdatePayload,
  getGroupApplicationFormDisplayName,
  GroupApplicationFormRow,
  GroupApplicationFormType,
  GroupApplicationFormValues,
  hasIncompleteGroupApplicationForms,
  serializeGroupApplicationForms,
} from 'group/helpers/groupApplicationForms'

const applicationSchema = yup.object({
  useDefaultStartingInstructions: yup.boolean().required(),
  applicationStartInstructions: yup.string().nullable().notRequired(),
  useDefaultCompletionInstructions: yup.boolean().required(),
  applicationCompletionInstructions: yup.string().nullable().notRequired(),
  forms: yup.array().notRequired(),
})

const useStyles = makeStyles(theme => ({
  modalContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    width: '100%',
    boxSizing: 'border-box',
  },
  instructionSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    width: '100%',
    boxSizing: 'border-box',
  },
  choiceSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
    width: '100%',
  },
  sectionTitle: {
    fontFamily: 'Nunito',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',
    letterSpacing: '0.5px',
    margin: 0,
  },
  radioIndent: {
    boxSizing: 'border-box',
    width: '100%',
    paddingLeft: '20px',
    '& .MuiFormGroup-root': {
      gap: '10px',
    },
    '& .MuiFormControlLabel-root': {
      margin: 0,
      alignItems: 'center',
      minHeight: '20px',
    },
    '& .MuiRadio-root': {
      padding: 0,
    },
    '& .MuiIconButton-root': {
      padding: 0,
    },
    '& .MuiFormControlLabel-label': {
      marginLeft: '13px',
      lineHeight: '20px',
    },
  },
  formsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    width: '100%',
    boxSizing: 'border-box',
    paddingTop: '10px',
  },
  formsLabel: {
    fontFamily: 'Nunito',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',
    letterSpacing: '0.5px',
    margin: 0,
  },
  formRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    width: '100%',
    marginBottom: '10px',
    cursor: 'grab',
  },
  formRowDragging: {
    opacity: 1,
    background: '#F8FAFF',
  },
  dragHandle: {
    display: 'inline-flex',
    alignItems: 'center',
    flexShrink: 0,
    padding: '4px',
    cursor: 'grab',
    color: '#74777F',
  },
  formSelector: {
    flex: '1 1 auto',
    minWidth: 0,
  },
  requiredLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    flexShrink: 0,
    cursor: 'pointer',
    fontFamily: 'Nunito, sans-serif',
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '20px',
    letterSpacing: '0.25px',
    color: '#000000',
  },
  checkboxRoot: {
    padding: '6px 8px',
    color: '#74777F',
    '&$checkboxChecked': {
      color: '#0F5DB5',
    },
  },
  checkboxChecked: {},
  checkboxIcon: {
    fontSize: '20px',
  },
  deleteIcon: {
    cursor: 'pointer',
    color: '#000000',
    flexShrink: 0,
  },
  addButtons: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    '& > *': {
      padding: '5px 20px',
    },
  },
  dropContainer: {
    display: 'block',
    width: '100%',
    position: 'relative',
  },
}))

const formTypeLabels: Record<GroupApplicationFormType, string> = {
  waiver: 'Waiver',
  questionnaire: 'Questionnaire',
  identification: 'Credential ID',
}

const formTypePlaceholders: Record<GroupApplicationFormType, string> = {
  waiver: 'Select Waiver',
  questionnaire: 'Select Questionnaire',
  identification: 'Select Credential ID',
}

interface SelectOption {
  id: number
  name: string
}

export interface EditGroupApplicationDialogProps {
  showing: boolean
  close: () => void
  complete: (value: boolean) => void
  group: {
    id: number
    applicationStartInstructions?: string | null
    applicationCompletionInstructions?: string | null
  }
  credentials: any[]
  questionSets: any[]
  onSubmitGroup: (payload: {
    applicationStartInstructions?: string | null
    applicationCompletionInstructions?: string | null
  }) => Promise<unknown>
  onCredentialsSaved?: (credentials: any[]) => void
  onQuestionSetsSaved?: (questionSets: any[]) => void
}

const getFormRowDragStyle = (
  isDragging: boolean,
  style: React.CSSProperties | undefined,
  container: HTMLDivElement | null,
) => {
  if (!isDragging || !style || !container) {
    return style
  }

  const { left, width } = container.getBoundingClientRect()

  return {
    ...style,
    left,
    width,
    opacity: 1,
    background: '#F8FAFF',
    boxSizing: 'border-box' as const,
  }
}

export default function EditGroupApplicationDialog({
  showing,
  close,
  complete,
  group,
  credentials,
  questionSets,
  onSubmitGroup,
  onCredentialsSaved,
  onQuestionSetsSaved,
}: EditGroupApplicationDialogProps) {
  const classes = useStyles()
  const showError = useErrorNotification()
  const formsDropContainerRef = useRef<HTMLDivElement | null>(null)
  const isCredentialEnabled = useFeatureEnabled('CredentialIDs')
  const isQuestionnaireAsSurveyEnabled = useFeatureEnabled('QuestionnaireAsSurvey')

  const { credentials: organizationCredentials } = useOrganizationCredentials()
  const [{ questionnaires }] = useQuestionnairesCollection() as unknown as [{ questionnaires: Questionnaire[] }]

  const [setGroupCredentials] = useSetGroupCredentials(onCredentialsSaved) as [
    (groupId: number, credentials: Array<{ id: number; required: boolean; displayOrder: number }>) => Promise<unknown>,
    { loading: boolean },
  ]
  const [setGroupQuestionSets] = useSetGroupQuestionSets(onQuestionSetsSaved) as [
    (groupId: number, questionSets: Array<Record<string, unknown>>) => Promise<unknown>,
    { loading: boolean },
  ]

  const initialValues = useMemo(
    () =>
      buildGroupApplicationInitialValues(group, {
        credentials,
        questionSets,
        isQuestionnaireAsSurveyEnabled,
      }),
    [credentials, group, isQuestionnaireAsSurveyEnabled, questionSets],
  )

  const waiverOptions = useMemo(
    () =>
      organizationCredentials
        .filter(({ credential }) => credential.type === 'waiver')
        .map(({ credential, version }) => ({
          id: credential.id,
          name: version.title,
        })),
    [organizationCredentials],
  )

  const identificationOptions = useMemo(
    () =>
      organizationCredentials
        .filter(({ credential }) => (credential.type as string) === 'identification')
        .map(({ credential, version }) => ({
          id: credential.id,
          name: version.title,
        })),
    [organizationCredentials],
  )

  const questionnaireOptions = useMemo(
    () =>
      filter(questionnaires, questionnaire => questionnaire.status === QuestionnaireStatus.ACTIVE).map(
        ({ id, name }) => ({
          id,
          name,
        }),
      ) as SelectOption[],
    [questionnaires],
  )

  const getOptionsForType = useCallback(
    (type: GroupApplicationFormType, selectedForms: GroupApplicationFormRow[]) => {
      const selectedIds = new Set(
        selectedForms.filter(form => form.type === type && form.id).map(form => form.id as number),
      )

      if (type === 'waiver') {
        return waiverOptions.filter(option => !selectedIds.has(option.id))
      }

      if (type === 'identification') {
        return identificationOptions.filter(option => !selectedIds.has(option.id))
      }

      return questionnaireOptions.filter(option => !selectedIds.has(option.id))
    },
    [identificationOptions, questionnaireOptions, waiverOptions],
  )

  const handleSubmit = useCallback(
    async (values: GroupApplicationFormValues) => {
      if (hasIncompleteGroupApplicationForms(values.forms)) {
        showError('Please select an option for each added form before saving.')
        return
      }

      const instructionsDirty = areGroupApplicationInstructionsDirty(values, initialValues)
      const formsDirty = areGroupApplicationFormsDirty(values.forms, initialValues.forms)

      if (instructionsDirty) {
        await onSubmitGroup(buildGroupApplicationInstructionsUpdatePayload(values, initialValues))
      }

      if (formsDirty) {
        const saves = []

        if (areGroupApplicationCredentialsDirty(values.forms, initialValues.forms, isQuestionnaireAsSurveyEnabled)) {
          saves.push(
            setGroupCredentials(
              group.id,
              serializeGroupApplicationForms(values.forms, isQuestionnaireAsSurveyEnabled).credentials,
            ),
          )
        }

        if (areGroupApplicationQuestionSetsDirty(values.forms, initialValues.forms, isQuestionnaireAsSurveyEnabled)) {
          saves.push(
            setGroupQuestionSets(
              group.id,
              serializeGroupApplicationForms(values.forms, isQuestionnaireAsSurveyEnabled).questionSets,
            ),
          )
        }

        if (saves.length) {
          await Promise.all(saves)
        }
      }

      complete(true)
    },
    [
      complete,
      group.id,
      initialValues,
      isQuestionnaireAsSurveyEnabled,
      onSubmitGroup,
      setGroupCredentials,
      setGroupQuestionSets,
      showError,
    ],
  )

  const handleDragEnd = useCallback(
    (result: DropResult, forms: GroupApplicationFormRow[], setFieldValue: (field: string, value: unknown) => void) => {
      if (!result.destination) {
        return
      }

      const reorderedForms = [...forms]
      const [removed] = reorderedForms.splice(result.source.index, 1)
      reorderedForms.splice(result.destination.index, 0, removed)
      setFieldValue('forms', reorderedForms)
    },
    [],
  )

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={applicationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ submitForm, isSubmitting, values, setFieldValue, isValid }) => {
        const isDirty = areGroupApplicationValuesDirty(values, initialValues)
        const formsDropContainer = formsDropContainerRef.current

        const updateFormAtIndex = (index: number, updates: Partial<GroupApplicationFormRow>) => {
          const nextForms = [...values.forms]
          nextForms[index] = { ...nextForms[index], ...updates }
          setFieldValue('forms', nextForms)
        }

        const handleAddFormType = (type: GroupApplicationFormType) => {
          setFieldValue('forms', [
            ...values.forms,
            {
              itemId: uniqueId('form-'),
              type,
              required: false,
            },
          ])
        }

        return (
          <FormModal
            showing={showing}
            close={close}
            id="edit-group-application-dialog"
            title="Edit Group Application"
            // Do NOT enable DraggablePaper here — react-beautiful-dnd forms reorder breaks (rows fly off-screen).
            draggable={false}
          >
            <FormModalScreen
              onBack={close}
              backText="Cancel"
              onNext={submitForm}
              nextText="Save"
              nextDisabled={!isDirty || !isValid}
              disabled={isSubmitting}
              nextIsLoading={isSubmitting}
            >
              <div className={classes.modalContent}>
                <div className={classes.instructionSection}>
                  <div className={classes.choiceSection}>
                    <span className={classes.sectionTitle}>Starting Instructions:</span>
                    <div className={classes.radioIndent}>
                      <RadioGroupFormField
                        name="useDefaultStartingInstructions"
                        valueIsBoolean
                        disabled={isSubmitting}
                        options={[
                          { label: 'Show default starting instructions', value: true },
                          { label: 'Show custom starting instructions', value: false },
                        ]}
                      />
                    </div>
                  </div>
                  {!values.useDefaultStartingInstructions && (
                    <Field
                      name="applicationStartInstructions"
                      label="Starting Instructions"
                      placeholder="Enter starting instructions that will be shown to group applicants"
                      component={CkEditorStyledField}
                      id="edit-group-application-start-instructions"
                      disabled={isSubmitting}
                    />
                  )}
                </div>

                <div className={classes.formsSection}>
                  <span className={classes.formsLabel}>Forms:</span>
                  <DragDropContext onDragEnd={result => handleDragEnd(result, values.forms, setFieldValue)}>
                    <Droppable droppableId="group-application-forms">
                      {provided => (
                        <div
                          ref={node => {
                            provided.innerRef(node)
                            formsDropContainerRef.current = node
                          }}
                          className={classes.dropContainer}
                          {...provided.droppableProps}
                        >
                          {values.forms.map((form, index) => {
                            const options = getOptionsForType(
                              form.type,
                              values.forms.filter((_, i) => i !== index),
                            )
                            const formDisplayName = getGroupApplicationFormDisplayName(form, {
                              waiverOptions,
                              identificationOptions,
                              questionnaireOptions,
                            })
                            const selectedOption = form.id ? { id: form.id, name: formDisplayName } : null

                            return (
                              <Draggable key={form.itemId} draggableId={form.itemId} index={index}>
                                {(draggableProvided, snapshot) => (
                                  <div
                                    ref={draggableProvided.innerRef}
                                    {...draggableProvided.draggableProps}
                                    className={`${classes.formRow} ${
                                      snapshot.isDragging ? classes.formRowDragging : ''
                                    }`}
                                    style={getFormRowDragStyle(
                                      snapshot.isDragging,
                                      draggableProvided.draggableProps.style,
                                      formsDropContainer,
                                    )}
                                  >
                                    <span {...draggableProvided.dragHandleProps} className={classes.dragHandle}>
                                      <DragIndicatorIcon fontSize="small" />
                                    </span>
                                    <div className={classes.formSelector}>
                                      <AdvancedAutocomplete<SelectOption>
                                        label={formTypeLabels[form.type]}
                                        placeholder={formTypePlaceholders[form.type]}
                                        options={
                                          selectedOption && !options.some(option => option.id === selectedOption.id)
                                            ? [selectedOption, ...options]
                                            : options
                                        }
                                        value={selectedOption}
                                        getOptionInfo={option => ({
                                          left: option.name,
                                          id: option.id,
                                        })}
                                        getOptionDisabled={option => selectedOption?.id === option.id}
                                        onChange={option => {
                                          if (!option || Array.isArray(option)) {
                                            return
                                          }

                                          updateFormAtIndex(index, {
                                            id: option.id,
                                            name: option.name,
                                            surveyId:
                                              form.type === 'questionnaire' && isQuestionnaireAsSurveyEnabled
                                                ? option.id
                                                : undefined,
                                          })
                                        }}
                                      />
                                    </div>
                                    <label className={classes.requiredLabel}>
                                      <Checkbox
                                        color="primary"
                                        classes={{
                                          root: classes.checkboxRoot,
                                          checked: classes.checkboxChecked,
                                        }}
                                        icon={<CheckBoxOutlineBlankIcon className={classes.checkboxIcon} />}
                                        checkedIcon={<CheckBoxIcon className={classes.checkboxIcon} />}
                                        checked={!!form.required}
                                        onChange={event => {
                                          const nextForms = [...values.forms]
                                          nextForms[index] = {
                                            ...nextForms[index],
                                            required: event.target.checked,
                                          }
                                          setFieldValue('forms', nextForms)
                                        }}
                                      />
                                      Required
                                    </label>
                                    <DeleteIcon
                                      className={classes.deleteIcon}
                                      onClick={() => {
                                        setFieldValue(
                                          'forms',
                                          values.forms.filter((_, formIndex) => formIndex !== index),
                                        )
                                      }}
                                    />
                                  </div>
                                )}
                              </Draggable>
                            )
                          })}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                  <div className={classes.addButtons}>
                    {isCredentialEnabled && (
                      <InlineAddButton onClick={() => handleAddFormType('identification')} text="Add Credential ID" />
                    )}
                    <InlineAddButton onClick={() => handleAddFormType('questionnaire')} text="Add Questionnaire" />
                    <InlineAddButton onClick={() => handleAddFormType('waiver')} text="Add Waiver" />
                  </div>
                </div>

                <div className={classes.instructionSection}>
                  <div className={classes.choiceSection}>
                    <span className={classes.sectionTitle}>Completion Instructions:</span>
                    <div className={classes.radioIndent}>
                      <RadioGroupFormField
                        name="useDefaultCompletionInstructions"
                        valueIsBoolean
                        disabled={isSubmitting}
                        options={[
                          { label: 'Show default completion instructions', value: true },
                          { label: 'Show custom completion instructions', value: false },
                        ]}
                      />
                    </div>
                  </div>
                  {!values.useDefaultCompletionInstructions && (
                    <Field
                      name="applicationCompletionInstructions"
                      label="Completion Instructions"
                      placeholder="Enter completion instructions that will be shown to group applicants"
                      component={CkEditorStyledField}
                      id="edit-group-application-completion-instructions"
                      disabled={isSubmitting}
                    />
                  )}
                </div>
              </div>
            </FormModalScreen>
          </FormModal>
        )
      }}
    </Formik>
  )
}
